import { Theme } from "@mui/material";
import { VARIABLE_DATA_ATTR } from "@simplyk/common";
import { $getRoot, $isDecoratorNode, $isTextNode, $isElementNode, ElementNode, LexicalNode, TextNode } from "lexical";

/**
 * Checks if the editor content is empty or contains only whitespace
 * @returns {boolean} True if the editor is empty or contains only whitespace
 */
export function $isEmpty() {
  const root = $getRoot();
  const firstChild = root.getFirstChild();

  if (!firstChild) {
    return true;
  }

  return $isWhitespace(root);
}

/**
 * Recursively checks if a node contains only whitespace
 * @param {ElementNode} node The node to check
 * @returns {boolean} True if the node contains only whitespace
 */
export function $isWhitespace(node: ElementNode): boolean {
  for (const child of node.getChildren()) {
    if (
      ($isElementNode(child) && !$isWhitespace(child)) ||
      ($isTextNode(child) && child.getTextContent().trim() !== "") ||
      $isDecoratorNode(child) // decorator nodes are arbitrary
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Recursively collects all text nodes from a Lexical element tree.
 * Must be called inside an editor.update() or editor.read() callback.
 */
export function $getAllTextNodes(root: ElementNode): TextNode[] {
  const textNodes: TextNode[] = [];
  const collect = (node: LexicalNode) => {
    if ($isTextNode(node)) {
      textNodes.push(node);
    } else if ($isElementNode(node)) {
      node.getChildren().forEach(collect);
    }
  };
  root.getChildren().forEach(collect);
  return textNodes;
}

/**
 * Checks if a string value represents rich text content by checking for the "LXCL:" prefix
 * @param {string} value The string to check
 * @returns {boolean} True if the string represents rich text content
 */

export function isRichText(value: string) {
  return value.startsWith("LXCL:");
}

/**
 * Serializes HTML content by prefixing it with "LXCL:" to mark it as rich text (to differentiate from plain text already in the database)
 * @param {string} outputHtml The HTML string to serialize
 * @returns {string} The serialized HTML string prefixed with "LXCL:"
 */
export function serialize(outputHtml: string) {
  return `LXCL:${outputHtml}`;
}

/**
 * Deserializes input content, determining if it's HTML or plain text
 * @param {string | null | undefined} inputHtmlOrString The input content to deserialize
 * @returns {{ isLexicalRichText: boolean, value: string }} Object containing the deserialized content and type flag
 */
export function deserialize(inputHtmlOrString: string | null | undefined) {
  if (!inputHtmlOrString) {
    return {
      isLexicalRichText: false,
      value: "",
    };
  }

  if (isRichText(inputHtmlOrString)) {
    return {
      isLexicalRichText: true,
      value: inputHtmlOrString.replace(/^LXCL:/, ""),
    };
  }

  return {
    isLexicalRichText: false,
    value: inputHtmlOrString,
  };
}

export const getRichTextInputTextStyles = (theme: Theme) => ({
  ["& p"]: {
    margin: 0,
  },
  ["& .lxcl-bold"]: {
    fontWeight: theme.typography.fontWeightBold,
  },
  ["& .lxcl-italic"]: {
    fontStyle: "italic",
  },
  ["& .lxcl-underline"]: {
    textDecoration: "underline",
  },
  ["& .lxcl-listItem::marker"]: {
    color: "var(--listitem-marker-color, currentColor)",
  },
  // The nested-listitem <li> is a pure container wrapping the nested <ul>.
  // Without this, it renders its own bullet alongside the child list's bullet,
  // producing a doubled "• ◦" on the same line.
  ["& .lxcl-nested-listitem"]: {
    listStyleType: "none",
  },
});

export const getRichTextInputLinkStyles = (linkColor: string) => ({
  ["& .lxcl-link"]: {
    color: `${linkColor} !important`,
    textDecoration: "none",
  },
  ["& .lxcl-link > span"]: {
    textDecoration: "underline",
  },
});

const INLINE_FORMATTING_TAGS = new Set(["B", "STRONG", "EM", "I", "U", "S", "SPAN"]);

type InlineColorLeaf = {
  node: Node;
  color?: string;
};

export const getSignificantDomChildren = (domNode: Node): Node[] => {
  return Array.from(domNode.childNodes).filter((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      return Boolean(child.textContent?.trim());
    }
    return true;
  });
};

export const extractColorFromElement = (element: Element): string | undefined => {
  if (element.hasAttribute(VARIABLE_DATA_ATTR)) {
    return undefined;
  }
  const style = element.getAttribute("style") || "";
  const colorMatch = style.match(/(?:^|;\s*)color:\s*([^;]+)/i);
  return colorMatch ? colorMatch[1].trim() : undefined;
};

const isInlineFormattingElement = (node: Node): node is Element =>
  node instanceof Element && INLINE_FORMATTING_TAGS.has(node.tagName) && !node.hasAttribute(VARIABLE_DATA_ATTR);

export const getInlineColorLeaves = (domNode: Node, inheritedColor?: string): InlineColorLeaf[] =>
  getSignificantDomChildren(domNode).flatMap((child) => {
    if (isInlineFormattingElement(child)) {
      return getInlineColorLeaves(child, extractColorFromElement(child) ?? inheritedColor);
    }
    return [{ node: child, color: inheritedColor }];
  });
